import type { Locale, UpdateSelectionRequest } from '@ralph/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import {
  applySelection,
  browseConnection,
  checkNow,
  createConnection,
  createFolder,
  disconnectFolder,
  fetchAvailability,
  fetchConnections,
  fetchFolder,
  fetchFolders,
  fetchItems,
  fetchLeftOut,
  fetchPreview,
  fetchS3Policy,
  fetchSyncs,
  type ItemFilter,
  type KeylessConnectionInput,
  pauseFolder,
  previewSelection,
  putBack,
  refetchItem,
  removeConnection,
  resumeFolder,
  retryFailed,
  startFolder,
  startSignIn,
  updateFolder
} from '@/api/connectors.ts'
import { useSession } from '@/hooks/use-api.ts'

/**
 * Connected folders (PRD-25) — the queries and mutations behind the tab.
 *
 * Two polling rules, each for a state a person is watching: a folder that is counting or
 * syncing is re-read every two seconds, and a folder that has queued files (the first sync
 * filling) every four. Everything else is read on demand, and every mutation that changes
 * what a knowledge base holds also invalidates its reading and its documents, because the
 * card, the list and the table all answer from those.
 */

/** What a folder write can change in the knowledge plane: the lists, the base, its documents. */
const invalidateKnowledge = (client: ReturnType<typeof useQueryClient>) => {
  void client.invalidateQueries({ queryKey: ['knowledge-bases', 'list'] })
  void client.invalidateQueries({ queryKey: ['knowledge-bases', 'base'] })
  void client.invalidateQueries({ queryKey: ['knowledge-bases', 'documents'] })
  void client.invalidateQueries({ queryKey: ['knowledge-bases', 'folders'] })
}

export const connectorKeys = {
  availability: ['connectors', 'availability'] as const,
  connections: ['connectors', 'connections'] as const,
  folders: (baseId: string) => ['connectors', 'folders', baseId] as const,
  folder: (id: string) => ['connectors', 'folder', id] as const,
  preview: (id: string) => ['connectors', 'preview', id] as const,
  items: (id: string, parent: string | null, filter: ItemFilter, query: string) =>
    ['connectors', 'items', id, parent ?? '', filter, query] as const,
  leftOut: (id: string) => ['connectors', 'left-out', id] as const,
  syncs: (id: string) => ['connectors', 'syncs', id] as const,
  s3Policy: (bucket: string, prefix: string) => ['connectors', 's3-policy', bucket, prefix] as const
}

const invalidateFolder = (client: ReturnType<typeof useQueryClient>, folderId: string, sourceId?: string) => {
  void client.invalidateQueries({ queryKey: connectorKeys.folder(folderId) })
  void client.invalidateQueries({ queryKey: connectorKeys.preview(folderId) })
  void client.invalidateQueries({ queryKey: connectorKeys.syncs(folderId) })
  void client.invalidateQueries({ queryKey: connectorKeys.leftOut(folderId) })
  void client.invalidateQueries({ queryKey: ['connectors', 'items', folderId] })
  if (sourceId) void client.invalidateQueries({ queryKey: connectorKeys.folders(sourceId) })
  void client.invalidateQueries({ queryKey: ['connectors', 'folders'] })
  invalidateKnowledge(client)
}

export const useConnectorAvailability = (enabled = true) =>
  useQuery({ queryKey: connectorKeys.availability, queryFn: fetchAvailability, enabled, staleTime: 5 * 60_000 })

export const useConnections = (enabled = true) =>
  useQuery({ queryKey: connectorKeys.connections, queryFn: fetchConnections, enabled })

export const useStartSignIn = () => useMutation({ mutationFn: startSignIn })

export const useCreateConnection = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: KeylessConnectionInput) => createConnection(input),
    onSuccess: () => void client.invalidateQueries({ queryKey: connectorKeys.connections })
  })
}

export const useRemoveConnection = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => removeConnection(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: connectorKeys.connections })
      void client.invalidateQueries({ queryKey: ['connectors', 'folders'] })
      invalidateKnowledge(client)
    }
  })
}

export const useBrowse = () =>
  useMutation({
    mutationFn: ({ connectionId, ...input }: { connectionId: string } & Parameters<typeof browseConnection>[1]) =>
      browseConnection(connectionId, input)
  })

export const useS3Policy = (bucket: string, prefix: string, enabled: boolean) =>
  useQuery({
    queryKey: connectorKeys.s3Policy(bucket, prefix),
    queryFn: () => fetchS3Policy(bucket, prefix),
    enabled: enabled && bucket.length >= 3
  })

/**
 * The documents a folder loads land in the base's own list, which nothing else would
 * refetch while the folder tab is the thing polling. When a folder's synced count moves,
 * the documents and the base move with it.
 */
const useFollowSynced = (folders: Array<{ stats: { synced: number }; status: string }> | undefined) => {
  const client = useQueryClient()
  const signature = folders ? folders.map((f) => `${f.status}:${f.stats.synced}`).join(',') : ''
  const last = useRef(signature)
  useEffect(() => {
    if (signature === last.current) return
    last.current = signature
    invalidateKnowledge(client)
  }, [signature, client])
}

export const useFolders = (baseId: string | undefined) => {
  const query = useFoldersQuery(baseId)
  useFollowSynced(query.data?.items)
  return query
}

const useFoldersQuery = (baseId: string | undefined) =>
  useQuery({
    queryKey: connectorKeys.folders(baseId ?? ''),
    queryFn: () => fetchFolders(baseId as string),
    enabled: Boolean(baseId),
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? []
      const live = items.some((f) => f.syncing || (f.status === 'previewing' && f.lastSyncAt === null))
      const filling = items.some((f) => f.stats.queued > 0 && f.status === 'active')
      return live ? 2_000 : filling ? 4_000 : false
    }
  })

export const useFolder = (id: string | undefined) => {
  const query = useFolderQuery(id)
  useFollowSynced(query.data ? [query.data] : undefined)
  return query
}

const useFolderQuery = (id: string | undefined) =>
  useQuery({
    queryKey: connectorKeys.folder(id ?? ''),
    queryFn: () => fetchFolder(id as string),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const folder = query.state.data
      if (!folder) return false
      if (folder.syncing || (folder.status === 'previewing' && folder.lastSyncAt === null)) return 2_000
      if (folder.stats.queued > 0 && folder.status === 'active') return 4_000
      return false
    }
  })

/**
 * Reads what the count has found so far, polling while it runs. When the folder's own poll
 * sees the walk finish it stops this one too — and cancels a tick that may not have fired,
 * which would leave the summary at whatever page it last saw. So the end of counting is an
 * explicit refetch, not a hope that the last tick landed after the last item did.
 */
export const usePreview = (id: string | undefined, counting: boolean) => {
  const client = useQueryClient()
  useEffect(() => {
    if (!counting && id) void client.invalidateQueries({ queryKey: connectorKeys.preview(id) })
  }, [counting, id, client])
  return useQuery({
    queryKey: connectorKeys.preview(id ?? ''),
    queryFn: () => fetchPreview(id as string),
    enabled: Boolean(id),
    refetchInterval: counting ? 2_500 : false
  })
}

export const useItems = (
  id: string | undefined,
  input: { parent: string | null; filter: ItemFilter; query: string; offset?: number },
  refresh = false
) => {
  const client = useQueryClient()
  // The same end-of-count refetch as the preview: the level shown must be the finished one.
  useEffect(() => {
    if (!refresh && id) void client.invalidateQueries({ queryKey: ['connectors', 'items', id] })
  }, [refresh, id, client])
  return useQuery({
    queryKey: [...connectorKeys.items(id ?? '', input.parent, input.filter, input.query), input.offset ?? 0] as const,
    queryFn: () =>
      fetchItems(id as string, {
        parent: input.parent,
        filter: input.filter,
        query: input.query,
        offset: input.offset ?? 0,
        limit: 100
      }),
    enabled: Boolean(id),
    refetchInterval: refresh ? 3_000 : false
  })
}

export const useLeftOut = (id: string | undefined) =>
  useQuery({
    queryKey: connectorKeys.leftOut(id ?? ''),
    queryFn: () => fetchLeftOut(id as string),
    enabled: Boolean(id)
  })

export const useSyncs = (id: string | undefined) =>
  useQuery({ queryKey: connectorKeys.syncs(id ?? ''), queryFn: () => fetchSyncs(id as string), enabled: Boolean(id) })

export const useCreateFolder = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: Parameters<typeof createFolder>[0]) => createFolder(input),
    onSuccess: (folder) => invalidateFolder(client, folder.id, folder.sourceId)
  })
}

export const useStartFolder = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ack, ackText, ackLocale }: { id: string; ack?: true; ackText?: string; ackLocale?: Locale }) =>
      startFolder(id, {
        ...(ack ? { ack } : {}),
        ...(ackText ? { ackText } : {}),
        ...(ackLocale ? { ackLocale } : {})
      }),
    onSuccess: (folder) => invalidateFolder(client, folder.id, folder.sourceId)
  })
}

export const usePreviewSelection = () =>
  useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateSelectionRequest }) => previewSelection(id, request)
  })

export const useApplySelection = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateSelectionRequest }) => applySelection(id, request),
    onSuccess: (result) => invalidateFolder(client, result.folder.id, result.folder.sourceId)
  })
}

export const usePutBack = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, remoteId }: { id: string; remoteId: string }) => putBack(id, remoteId),
    onSuccess: (folder) => invalidateFolder(client, folder.id, folder.sourceId)
  })
}

export const useCheckNow = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, walk }: { id: string; walk?: boolean }) => checkNow(id, walk ?? false),
    onSuccess: (result) => invalidateFolder(client, result.folder.id, result.folder.sourceId)
  })
}

export const usePauseFolder = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => pauseFolder(id),
    onSuccess: (folder) => invalidateFolder(client, folder.id, folder.sourceId)
  })
}

export const useResumeFolder = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => resumeFolder(id),
    onSuccess: (folder) => invalidateFolder(client, folder.id, folder.sourceId)
  })
}

export const useRetryFailed = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => retryFailed(id),
    onSuccess: (result) => invalidateFolder(client, result.folder.id, result.folder.sourceId)
  })
}

export const useUpdateFolder = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { intervalMs?: number; connectionId?: string } }) =>
      updateFolder(id, patch),
    onSuccess: (folder) => invalidateFolder(client, folder.id, folder.sourceId)
  })
}

export const useDisconnectFolder = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, withdraw }: { id: string; withdraw: boolean }) => disconnectFolder(id, withdraw),
    onSuccess: (_result, variables) => invalidateFolder(client, variables.id)
  })
}

export const useRefetchItem = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (itemId: string) => refetchItem(itemId),
    onSuccess: () => {
      invalidateKnowledge(client)
      void client.invalidateQueries({ queryKey: ['connectors'] })
    }
  })
}

/** Who is looking: the id and whether they administer, for the controls only an owner may use. */
export const useViewer = (): { id: string | null; admin: boolean } => {
  const session = useSession()
  const user = session.data?.user
  return { id: user?.id ?? null, admin: (user?.roles ?? []).includes('admin') }
}
